import { Injectable, NgZone } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import { discussionSocketEvents } from "../../global/constants";
import { DiscussionSocketService } from "../discussion/discussion-socket.service";

@Injectable({
  providedIn: "root",
})
export class SupportSocketService {
  private bound = false;
  private activeDiscussionId = "";

  readonly newMessage$ = new Subject<any>();
  readonly typingUsers$ = new BehaviorSubject<any[]>([]);

  constructor(
    private readonly discussionSocketService: DiscussionSocketService,
    private readonly ngZone: NgZone
  ) {}

  initialize() {
    this.discussionSocketService.createSocketConnection();

    if (this.bound) {
      return;
    }

    this.bound = true;

    this.discussionSocketService.subscribe("connect", () => {
      if (!this.activeDiscussionId) {
        return;
      }

      this.discussionSocketService.emit(discussionSocketEvents.JOIN_ROOM, {
        id: this.activeDiscussionId,
      });
    });

    this.discussionSocketService.subscribe(
      discussionSocketEvents.NEW_MESSAGE,
      (message: any) => {
        if (
          !this.activeDiscussionId ||
          String(message?.discussion_id || "") !== this.activeDiscussionId
        ) {
          return;
        }

        this.ngZone.run(() => this.newMessage$.next(message));
      }
    );

    this.discussionSocketService.subscribe(
      discussionSocketEvents.CHAT_ROOM_TYPING,
      (payload: any) => {
        const users = Object.values(payload || {});
        this.ngZone.run(() =>
          this.typingUsers$.next(Array.isArray(users) ? users : [])
        );
      }
    );
  }

  joinRoom(discussionId: string) {
    if (!discussionId) {
      return;
    }

    this.initialize();

    if (this.activeDiscussionId && this.activeDiscussionId !== discussionId) {
      this.leaveRoom(this.activeDiscussionId);
    }

    this.activeDiscussionId = discussionId;
    this.discussionSocketService.emit(discussionSocketEvents.JOIN_ROOM, {
      id: discussionId,
    });
  }

  leaveRoom(discussionId?: string) {
    const roomId = discussionId || this.activeDiscussionId;
    if (!roomId) {
      return;
    }

    this.discussionSocketService.emit(discussionSocketEvents.LEAVE_ROOM, {
      id: roomId,
    });

    if (roomId === this.activeDiscussionId) {
      this.activeDiscussionId = "";
      this.typingUsers$.next([]);
    }
  }

  sendTyping(message: string | null) {
    if (!this.activeDiscussionId) {
      return;
    }

    this.discussionSocketService.emit(discussionSocketEvents.JOIN_ROOM, {
      id: this.activeDiscussionId,
    });
    this.discussionSocketService.emit(discussionSocketEvents.TYPING, {
      id: this.activeDiscussionId,
      message,
    });
  }

  sendMessage(payload: {
    discussionId: string;
    message: string;
    uploadFile?: any[];
    support_thread_id?: string;
    support_issue_session_id?: string;
    parent_id?: string;
  }) {
    return new Promise<any>((resolve) => {
      const socketPayload = {
        id: payload.discussionId,
        type: "discussion",
        message: payload.message,
        uploadFile: payload.uploadFile || [],
        support_thread_id: payload.support_thread_id,
        support_issue_session_id: payload.support_issue_session_id,
        parent_id: payload.parent_id,
      };
      this.discussionSocketService.emit(
        discussionSocketEvents.MESSAGE,
        socketPayload,
        (response: any) => resolve(response)
      );
    });
  }
}
